import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ClipboardText, LockSimple, Prohibit, Warning, CheckSquare, WarningCircle } from "@phosphor-icons/react";
import { auth } from "../../../auth/firebase";
import { getAuthErrorMessage, hasPendingTryNowJourney, signupApplicant } from "../../../hallticket-lib";
import { useAppLayout } from "../../../layout";
import authBrandIcon from "../../../assets/design/HallTicket logo/app-icon/square-dark/hallticket-icon-256-dark.png";
import "./TermsScreen.css";

const TERMS_HIGHLIGHTS = [
  {
    icon: <LockSimple size={18} weight="fill" />,
    title: "Your data stays private",
    body: "Phone, scores & documents are encrypted (AES-256). We never sell your data.",
    tone: "info",
  },
  {
    icon: <Warning size={18} weight="fill" />,
    title: "Admission scores are indicative",
    body: "College picks are estimates from past cut-offs - not guarantees. Verify with colleges directly.",
    tone: "warning",
  },
  {
    icon: <CheckSquare size={18} weight="fill" />,
    title: "You can delete your account",
    body: "Request full data deletion any time via support. Processed within 30 days.",
    tone: "success",
  },
  {
    icon: <Prohibit size={18} weight="fill" />,
    title: "No scraping or misuse",
    body: "Commercial use, bots, or scraping is prohibited. Violations may result in suspension.",
    tone: "danger",
  },
];

export function TermsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoader, showToast } = useAppLayout();
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  const normalizeMobile = (raw: string | null | undefined): string | null => {
    if (!raw) {
      return null;
    }

    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\+91\d{10}$/.test(trimmed)) {
      return trimmed;
    }

    if (/^\d{10}$/.test(trimmed)) {
      return `+91${trimmed}`;
    }

    if (/^91\d{10}$/.test(trimmed)) {
      return `+${trimmed}`;
    }

    return null;
  };

  const toMobileNo = (mobileWithCountryCode: string): string => {
    return mobileWithCountryCode.replace(/^\+91/, "");
  };

  const handleDecline = () => {
    if (isSubmitting) {
      return;
    }
    setShowDeclineConfirm(true);
  };

  const handleConfirmDecline = () => {
    setShowDeclineConfirm(false);
    window.location.assign("https://www.hallticket.in/");
  };

  const handleAccept = async () => {
    if (!agreed || isSubmitting) {
      return;
    }

    const locationState = location.state as { mobile?: string; postAuthRedirect?: string } | null;
    const mobileFromState = locationState?.mobile;
    const postAuthRedirect = locationState?.postAuthRedirect === "/dashboard/results" ? locationState.postAuthRedirect : null;
    const mobile = normalizeMobile(mobileFromState) ?? normalizeMobile(auth.currentUser?.phoneNumber);

    if (!mobile) {
      showToast({
        severity: "error",
        summary: "Missing mobile number",
        detail: "Please sign in again to continue.",
      });
      navigate("/signin", { replace: true });
      return;
    }

    setIsSubmitting(true);
    setLoader(true);

    try {
      const refreshedIdToken = await auth.currentUser?.getIdToken(true);
      if (!refreshedIdToken) {
        throw new Error("Your session has expired. Please sign in again.");
      }

      await signupApplicant(
        {
          mobile,
          mobileNo: toMobileNo(mobile),
        },
        refreshedIdToken,
      );
      navigate(postAuthRedirect ?? (hasPendingTryNowJourney() ? "/dashboard/results" : "/dashboard"), { replace: true });
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Signup failed",
        detail: getAuthErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
      setLoader(false);
    }
  };

  return (
    <div className="auth-screen auth-screen--terms">
      <ConfirmDialog
        visible={showDeclineConfirm}
        onHide={() => setShowDeclineConfirm(false)}
        className="terms-decline-dialog"
        header="Confirm Exit"
        message={(
          <span className="terms-confirm-message">
            <WarningCircle size={22} weight="fill" />
            Are you sure you want to decline and exit HallTicket?
          </span>
        )}
        acceptLabel="Yes, Exit"
        rejectLabel="Cancel"
        accept={handleConfirmDecline}
        reject={() => setShowDeclineConfirm(false)}
      />
      <div className="auth-card terms-screen">
        <div className="brand">
          <div className="brand-icon"><img src={authBrandIcon} alt="HallTicket" className="brand-icon-img" /></div>
          <div className="brand-name">
            Hall<span>Ticket</span>
          </div>
        </div>
        <div className="tc-badge">
          <ClipboardText size={13} weight="duotone" />
          Terms & Conditions
        </div>
        <h2 className="tc-heading">Before you continue</h2>
        <p className="tc-intro">Here's what matters most — a plain-English summary of our terms before you dive in.</p>
        <div className="tc-highlights-grid">
          {TERMS_HIGHLIGHTS.map((section) => (
            <section key={section.title} className={`tc-highlight-card tc-highlight-card--${section.tone}`}>
              <div className="tc-highlight-icon">{section.icon}</div>
              <h4>{section.title}</h4>
              <p>{section.body}</p>
            </section>
          ))}
        </div>
        <div className="tc-readfull">
          <span>Want to read everything? View the complete Terms & Privacy Policy</span>
          <a href="https://www.hallticket.in/terms.html" target="_blank" rel="noreferrer">
            Read in full →
          </a>
        </div>
        <div className="tc-agree-row" onClick={() => setAgreed((prev) => !prev)}>
          <Checkbox inputId="terms-agree" checked={agreed} onChange={(e) => setAgreed(e.checked ?? false)} />
          <label htmlFor="terms-agree" className="tc-agree-text">
            I've read the key points above and agree to HallTicket's <a href="https://www.hallticket.in/terms.html" target="_blank" rel="noreferrer">Terms of Service and Privacy Policy</a>. I understand admission predictions are indicative, not guaranteed.
          </label>
        </div>
        <div className="tc-btn-row">
          <Button
            label="Decline"
            className="btn btn-outline"
            onClick={handleDecline}
            disabled={isSubmitting}
          />
          <Button
            label="Accept & Continue →"
            className="btn btn-primary"
            onClick={handleAccept}
            disabled={!agreed || isSubmitting}
            loading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
}

